// src/context/MiningContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from 'react';
import { ethers } from 'ethers';
import { WalletContext } from './WalletContext';
import { toast } from 'react-toastify';
import {
  loadMiningContract,
  loadCashSwapContract,
  loadStakingContract,
  loadTokenContract,
} from '../utils/loadBlockchain';
import { CircularProgress, Typography } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { NETWORK_CONFIG } from '../contracts/constants';

export const MiningContext = createContext();

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
  },
};

export const MiningProvider = ({ children }) => {
  const { account, provider } = useContext(WalletContext);

  // =========================
  // ====== State Values =====
  // =========================

  const [miningEventStatus, setMiningEventStatus] = useState({
    isActive: false,
    startTime: 0,
    endTime: 0,
    finalized: false,
    totalCollected: '0',
    totalRewardsDistributed: '0',
  });
  const [userReward, setUserReward] = useState('0');
  const [hasClaimedReward, setHasClaimedReward] = useState(false);
  const [userBoost, setUserBoost] = useState(0);
  const [isOwner, setIsOwner] = useState(false);

  // Updated: storing contributedLastHour too
  const [userContributions, setUserContributions] = useState({
    contributionCount: 0,
    contributionAmount: '0',
    minerNumber: 0,
    contributedLastHour: false,
  });

  const [isTransactionPending, setIsTransactionPending] = useState(false);
  const [showStakingModal, setShowStakingModal] = useState(false);
  const [totalMiners, setTotalMiners] = useState(0);
  const [stakingPositions, setStakingPositions] = useState([]);
  const [totalStaked, setTotalStaked] = useState('0');
  const [isInitialized, setIsInitialized] = useState(false);

  // =========================
  // ======= Refs ============
  // =========================

  const miningContractRef = useRef(null);
  const cashSwapContractRef = useRef(null);
  const stakingContractRef = useRef(null);
  const tokenContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);
  const lastInitAttemptRef = useRef(0);

  // =========================
  // ======= Functions =======
  // =========================

  /**
   * Simple retry wrapper for ephemeral RPC errors including rate limits
   */
  const retryCall = useCallback(async (fn, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await fn();
      } catch (error) {
        const errMsg = (error.message || '').toLowerCase();
        const dataMsg = (error?.data?.message || '').toLowerCase();
        const errorStr = JSON.stringify(error).toLowerCase();
        
        const isCommonRpcError =
          error.code === -32603 ||
          error.code === -32005 ||
          error.code === -32002 ||
          error?.data?.code === -32019 ||
          error?.data?.code === -32005 ||
          errMsg.includes("limit exceeded") ||
          dataMsg.includes("block is out of range") ||
          errMsg.includes("blockchain height exceeded") ||
          errMsg.includes("non-200 status code") ||
          errMsg.includes("bad result from backend") ||
          errMsg.includes("rate limit") ||
          errMsg.includes("rate exceeded") ||
          errMsg.includes("too many requests") ||
          errMsg.includes("call exception") ||
          errMsg.includes("missing revert data") ||
          errorStr.includes("rate limit") ||
          errorStr.includes("bad result");

        // If ephemeral, retry with longer delays for rate limits
        if (isCommonRpcError && i < maxAttempts - 1) {
          const delay = Math.pow(2, i) * 1500;
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
    return null;
  }, []);

  /**
   * Fetch the overall mining event status
   */
  const fetchMiningEventStatus = useCallback(async () => {
    if (!miningContractRef.current) return null;
    try {
      const status = await retryCall(() => miningContractRef.current.miningEvent());
      const updatedEventStatus = {
        isActive: status.isActive,
        startTime: status.startTime.toNumber(),
        endTime: status.endTime.toNumber(),
        finalized: status.finalized,
        totalCollected: ethers.utils.formatEther(status.totalCollected),
        totalRewardsDistributed: ethers.utils.formatEther(status.allocation),
      };
      setMiningEventStatus((prev) => (!isEqual(prev, updatedEventStatus) ? updatedEventStatus : prev));

      // total miners
      const totalMinersFetched = await retryCall(() => miningContractRef.current.getTotalMiners());
      setTotalMiners(totalMinersFetched.toNumber());
      return updatedEventStatus;
    } catch (error) {
      const errorString = JSON.stringify(error);
      const errorMsg = error.message || '';
      const dataMsg = error?.data?.message || '';
      const errorData = error?.data || {};

      const isCommonRpcError =
        errorString.includes("block is out of range") ||
        errorString.includes("non-200 status code") ||
        errorString.includes("blockchain height exceeded") ||
        errorString.includes("execution reverted") ||
        errorString.includes("missing revert data") ||
        errorString.includes("rate limit") ||
        errorString.includes("limit exceeded") ||
        errorString.includes("bad result") ||
        dataMsg.includes("block is out of range") ||
        errorMsg.includes("block is out of range") ||
        errorMsg.includes("missing revert data") ||
        errorMsg.includes("rate limit") ||
        errorMsg.includes("limit exceeded") ||
        errorMsg.includes("rate exceeded") ||
        errorMsg.includes("bad result") ||
        error.code === -32603 ||
        error.code === -32005 ||
        error.code === -32002 ||
        errorData.code === -32019 ||
        errorData.code === -32005 ||
        (errorData.error?.code === -32019) ||
        (errorData.jsonrpc === "2.0" && errorData.error);

      // Only log if not a common RPC error
      if (!isCommonRpcError) {
        console.error("Error fetching mining event status:", error);
        toast.error("Failed to fetch mining event data.");
      }
      return null;
    }
  }, [retryCall]);

  /**
   * Fetch user-specific mining data
   */
  const fetchUserMiningData = useCallback(
    async (currentMiningEventStatus = miningEventStatus) => {
      if (!miningContractRef.current || !account) return;
      try {
        const contributionAmount = await retryCall(() =>
          miningContractRef.current.getUserContribution(account)
        );
        const contributionCount = await retryCall(() =>
          miningContractRef.current.getUserContributionCount(account)
        );
        const minerNumber = await retryCall(() =>
          miningContractRef.current.getMinerNumber(account)
        );
        const claimedReward = await retryCall(() =>
          miningContractRef.current.hasUserClaimedReward(account)
        );

        let boost = 0;
        try {
          boost = await retryCall(() =>
            miningContractRef.current.boosterNFT().then((boosterAddr) => {
              const boosterContract = new ethers.Contract(
                boosterAddr,
                ["function getBoost(address) view returns (uint256)"],
                provider
              );
              return boosterContract.getBoost(account);
            })
          );
          boost = boost.toNumber();
        } catch (error) {
          console.warn("Could not fetch user boost:", error);
          boost = 0;
        }

        // ============== [NEW] Check if user contributed last hour ==============
        let contributedLastHour = false;
        try {
          contributedLastHour = await retryCall(() =>
            miningContractRef.current.didContributeLastHour(account)
          );
        } catch {
          contributedLastHour = false;
        }
        // =======================================================================

        // set user data
        setUserContributions({
          contributionCount: contributionCount.toNumber(),
          contributionAmount: ethers.utils.formatEther(contributionAmount),
          minerNumber: minerNumber.toNumber(),
          contributedLastHour, // new
        });
        setHasClaimedReward(claimedReward);
        setUserBoost(boost);

        // If event is finalized, fetch user reward
        const isFinalized =
          currentMiningEventStatus?.finalized ?? miningEventStatus.finalized;
        if (isFinalized && contributionAmount.gt(0)) {
          const rewardValue = await retryCall(() =>
            miningContractRef.current.calculateReward(account)
          );
          setUserReward(ethers.utils.formatEther(rewardValue));
        } else {
          setUserReward("0");
        }
      } catch (error) {
        const errorString = JSON.stringify(error);
        const errorMsg = error.message || '';
        const dataMsg = error?.data?.message || '';
        const errorData = error?.data || {};

        const isCommonRpcError =
          errorString.includes("block is out of range") ||
          errorString.includes("non-200 status code") ||
          errorString.includes("blockchain height exceeded") ||
          errorString.includes("execution reverted") ||
          errorString.includes("missing revert data") ||
          errorString.includes("rate limit") ||
          errorString.includes("bad result") ||
          dataMsg.includes("block is out of range") ||
          errorMsg.includes("block is out of range") ||
          errorMsg.includes("missing revert data") ||
          errorMsg.includes("rate limit") ||
          errorMsg.includes("limit exceeded") ||
          errorMsg.includes("rate exceeded") ||
          errorMsg.includes("bad result") ||
          error.code === -32603 ||
          error.code === -32005 ||
          error.code === -32002 ||
          errorData.code === -32019 ||
          errorData.code === -32005 ||
          (errorData.error?.code === -32019) ||
          (errorData.jsonrpc === "2.0" && errorData.error);

        if (!isCommonRpcError) {
          console.error("Error fetching user mining data:", error);
          toast.error("Failed to fetch user mining data.");
        }
      }
    },
    [account, miningEventStatus, retryCall, provider]
  );

  /**
   * Check if user is contract owner
   */
  const fetchOwnerStatus = useCallback(async () => {
    if (!miningContractRef.current || !account) return;
    try {
      const contractOwner = await retryCall(() => miningContractRef.current.owner());
      setIsOwner(account.toLowerCase() === contractOwner.toLowerCase());
    } catch (error) {
      console.error("Error fetching owner status:", error);
      toast.error("Failed to fetch owner status.");
    }
  }, [account, retryCall]);

  /**
   * Fetch user's staking positions
   */
  const fetchStakingPositions = useCallback(async () => {
    if (!stakingContractRef.current || !account) return;
    try {
      const positions = await retryCall(() =>
        stakingContractRef.current.getPositions(account)
      );
      const formattedPositions = await Promise.all(
        positions.map(async (pos) => ({
          id: pos.positionId.toNumber(),
          amount: ethers.utils.formatEther(pos.amount),
          stakingStartTimestamp: pos.stakingStartTimestamp.toNumber(),
          stakingEndTimestamp: pos.stakingEndTimestamp.toNumber(),
          stakingPeriod: pos.stakingPeriod,
          rewardsClaimed: pos.rewardsClaimed,
          isExpired: pos.stakingEndTimestamp.toNumber() < Math.floor(Date.now() / 1000),
        }))
      );
      setStakingPositions(formattedPositions);

      const totalStakedAmount = positions.reduce(
        (acc, p) => acc.add(p.amount),
        ethers.BigNumber.from(0)
      );
      setTotalStaked(ethers.utils.formatUnits(totalStakedAmount, 18));
    } catch (error) {
      console.error("Failed to fetch staking positions:", error);
      toast.error("Failed to fetch staking positions.");
    }
  }, [account, retryCall]);

  /**
   * Contribute function
   */
  const contributeFunction = useCallback(
    async (contributionAmountInWei) => {
      if (!miningContractRef.current) throw new Error("Mining contract not loaded.");
      try {
        setIsTransactionPending(true);
        const tx = await miningContractRef.current
          .connect(provider.getSigner())
          .contribute({ value: contributionAmountInWei });
        toast.info("Contribution transaction submitted...");
        await tx.wait();

        const updatedEventStatus = await fetchMiningEventStatus();
        await fetchUserMiningData(updatedEventStatus);

        toast.success("Contribution successful.");
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === "ACTION_REJECTED" ||
          (error.message || "").toLowerCase().includes("user rejected") ||
          (error.message || "").toLowerCase().includes("user denied")
        ) {
          return;
        } else if (error.data?.originalError?.error?.message) {
          toast.error(`Contribution failed: ${error.data.originalError.error.message}`);
        } else if (error.data?.message) {
          toast.error(`Contribution failed: ${error.data.message}`);
        } else {
          toast.error("Contribution failed.");
        }
        throw error;
      } finally {
        setIsTransactionPending(false);
      }
    },
    [provider, fetchMiningEventStatus, fetchUserMiningData]
  );

  /**
   * Claim reward function
   */
  const claimMiningRewardFunction = useCallback(async () => {
    if (!miningContractRef.current) {
      toast.error("Mining contract not initialized");
      return false;
    }
    try {
      setIsTransactionPending(true);

      // optional check or calculation
      await retryCall(() => miningContractRef.current.calculateReward(account));

      const tx = await miningContractRef.current
        .connect(provider.getSigner())
        .claimReward({ gasLimit: 8000000 });

      toast.info("Claiming mining rewards...");
      const receipt = await tx.wait();

      if (receipt.status === 0) {
        throw new Error("Transaction failed. Please check your wallet for details.");
      }

      await fetchUserMiningData();
      toast.success("Mining rewards claimed successfully!");

      if (!miningEventStatus.finalized) {
        setShowStakingModal(true);
      }
      return true;
    } catch (error) {
      if (
        error.code === 4001 ||
        error.code === "ACTION_REJECTED" ||
        (error.message || "").toLowerCase().includes("user rejected") ||
        (error.message || "").toLowerCase().includes("user denied")
      ) {
        toast.info("Transaction was cancelled");
        return false;
      }

      console.error("Error claiming mining rewards:", error);

      if (error.message.includes("insufficient funds")) {
        toast.error("Insufficient funds for gas");
      } else if (error.message.includes("gas required exceeds")) {
        toast.error("Transaction requires more gas. Please try again with higher gas limit.");
      } else {
        toast.error("Failed to claim mining rewards. Check console for details.");
      }

      throw error;
    } finally {
      setIsTransactionPending(false);
    }
  }, [
    provider,
    fetchUserMiningData,
    miningEventStatus,
    account,
    retryCall
  ]);

  /**
   * Initialize all contracts
   */
  const initializeContracts = useCallback(async () => {
    if (!provider || !account || contractsInitializedRef.current) return;
    
    // Throttle: prevent repeated init attempts within 15 seconds
    const now = Date.now();
    if (now - lastInitAttemptRef.current < 15000) {
      return;
    }
    lastInitAttemptRef.current = now;

    try {
      const network = await provider.getNetwork();
      if (network.chainId !== NETWORK_CONFIG.chainId) {
        throw new Error(`Please connect to ${NETWORK_CONFIG.name}`);
      }

      const miningContract = await loadMiningContract(provider);
      if (!miningContract) {
        throw new Error("Mining contract failed to load");
      }

      const code = await provider.getCode(miningContract.address);
      if (code === "0x") {
        throw new Error(`Mining contract not deployed on ${NETWORK_CONFIG.name}`);
      }

      const cashSwapContract = await loadCashSwapContract(provider);
      const stakingContract = await loadStakingContract(provider);
      const tokenContract = await loadTokenContract(provider);

      if (!cashSwapContract || !stakingContract || !tokenContract) {
        throw new Error("Failed to initialize one or more contracts");
      }

      miningContractRef.current = miningContract;
      cashSwapContractRef.current = cashSwapContract;
      stakingContractRef.current = stakingContract;
      tokenContractRef.current = tokenContract;
      contractsInitializedRef.current = true;
      setIsInitialized(true);

      const updatedEventStatus = await fetchMiningEventStatus();
      if (account) {
        await fetchUserMiningData(updatedEventStatus);
        await fetchOwnerStatus();
        await fetchStakingPositions();
      }
    } catch (error) {
      const errorString = JSON.stringify(error);
      const errorMsg = error.message || '';
      const dataMsg = error?.data?.message || '';
      const errorData = error?.data || {};

      const isCommonRpcError =
        errorString.includes("block is out of range") ||
        errorString.includes("non-200 status code") ||
        errorString.includes("blockchain height exceeded") ||
        errorString.includes("execution reverted") ||
        errorString.includes("missing revert data") ||
        errorString.includes("rate limit") ||
        errorString.includes("bad result") ||
        dataMsg.includes("block is out of range") ||
        errorMsg.includes("block is out of range") ||
        errorMsg.includes("missing revert data") ||
        errorMsg.includes("rate limit") ||
        errorMsg.includes("limit exceeded") ||
        errorMsg.includes("rate exceeded") ||
        errorMsg.includes("bad result") ||
        error.code === -32603 ||
        error.code === -32005 ||
        error.code === -32002 ||
        errorData.code === -32019 ||
        errorData.code === -32005 ||
        (errorData.error?.code === -32019) ||
        (errorData.jsonrpc === "2.0" && errorData.error);

      if (!isCommonRpcError) {
        console.error("Error initializing contracts:", error);
        toast.error("Failed to initialize contracts.");
      } else {
        // Extend throttle on rate limit to avoid spamming RPC
        lastInitAttemptRef.current = Date.now() + 30000;
      }
      setIsInitialized(false);
    }
  }, [
    provider,
    account,
    fetchMiningEventStatus,
    fetchUserMiningData,
    fetchOwnerStatus,
    fetchStakingPositions
  ]);

  useEffect(() => {
    initializeContracts();
  }, [initializeContracts]);

  /**
   * Withdraw funds (owner only)
   */
  const withdrawFunds = useCallback(
    async (amount) => {
      if (!miningContractRef.current) {
        toast.error("Mining contract not loaded.");
        return;
      }
      try {
        setIsTransactionPending(true);
        const tx = await miningContractRef.current
          .connect(provider.getSigner())
          .withdrawFunds(amount);
        toast.info("Withdrawal transaction submitted...");
        await tx.wait();
        await fetchMiningEventStatus();
        await fetchStakingPositions();
        toast.success("Funds withdrawn successfully.");
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === "ACTION_REJECTED" ||
          (error.message || "").toLowerCase().includes("user rejected") ||
          (error.message || "").toLowerCase().includes("user denied")
        ) {
          return;
        } else if (error.data?.originalError?.error?.message) {
          toast.error(`Withdrawal failed: ${error.data.originalError.error.message}`);
        } else if (error.data?.message) {
          toast.error(`Withdrawal failed: ${error.data.message}`);
        } else {
          toast.error("Withdrawal failed.");
        }
        throw error;
      } finally {
        setIsTransactionPending(false);
      }
    },
    [provider, fetchMiningEventStatus, fetchStakingPositions]
  );

  // Listen for "UserContributed" events
  useEffect(() => {
    if (!miningContractRef.current || !account) return;
    try {
      const userContributedHandler = (user) => {
        if (user.toLowerCase() === account.toLowerCase()) {
          fetchUserMiningData();
        }
      };
      miningContractRef.current.on("UserContributed", userContributedHandler);
      return () => {
        if (miningContractRef.current) {
          miningContractRef.current.off("UserContributed", userContributedHandler);
        }
      };
    } catch (error) {
      console.error("Error setting up event listeners:", error);
    }
  }, [account, fetchUserMiningData]);

  // If account changes or newly connected, refresh data
  useEffect(() => {
    if (account && contractsInitializedRef.current) {
      fetchMiningEventStatus().then((updatedEventStatus) => {
        fetchUserMiningData(updatedEventStatus);
      });
      fetchOwnerStatus();
      fetchStakingPositions();
    } else {
      setUserContributions({
        contributionCount: 0,
        contributionAmount: "0",
        minerNumber: 0,
        contributedLastHour: false,
      });
      setUserReward("0");
      setHasClaimedReward(false);
      setIsOwner(false);
      setTotalMiners(0);
      setStakingPositions([]);
      setTotalStaked("0");
    }
  }, [
    account,
    fetchMiningEventStatus,
    fetchUserMiningData,
    fetchOwnerStatus,
    fetchStakingPositions,
  ]);

  // If event finalizes, re-fetch user data
  useEffect(() => {
    if (contractsInitializedRef.current && account) {
      fetchUserMiningData();
    }
  }, [miningEventStatus.finalized, account, fetchUserMiningData]);

  // Keep user Boost updated every 30s
  useEffect(() => {
    const updateBoost = async () => {
      if (!account || !provider || !miningContractRef.current || !isInitialized) return;

      try {
        const boosterAddr = await miningContractRef.current.boosterNFT();
        const boosterContract = new ethers.Contract(
          boosterAddr,
          ["function getBoost(address) view returns (uint256)"],
          provider
        );

        const boost = await boosterContract.getBoost(account);
        const boostValue = boost.toNumber();
        setUserBoost(boostValue);
      } catch (error) {
        const errMsg = (error.message || '').toLowerCase();
        const isRpcErr =
          error.code === -32005 || error.code === -32603 || error.code === 'SERVER_ERROR' ||
          errMsg.includes('bad result') ||
          errMsg.includes('rate limit') ||
          errMsg.includes('limit exceeded');
        if (!isRpcErr) {
          console.warn("Could not update user boost:", error);
        }
      }
    };

    updateBoost();
    const boostInterval = setInterval(updateBoost, 30000);
    return () => {
      clearInterval(boostInterval);
    };
  }, [account, provider, isInitialized]);

  // Provide context to the rest of the app
  const contextValue = useMemo(
    () => ({
      miningEventStatus,
      userReward,
      userContributions,
      isOwner,
      hasClaimedReward,
      userBoost,
      contribute: contributeFunction,
      claimMiningReward: claimMiningRewardFunction,
      withdrawFunds,
      isTransactionPending,
      showStakingModal,
      setShowStakingModal,
      stakingContractRef,
      tokenContractRef,
      totalMiners,
      stakingPositions,
      setStakingPositions,
      totalStaked,
      setTotalStaked,
      fetchStakingPositions,
      fetchUserMiningData,
      isInitialized,
    }),
    [
      miningEventStatus,
      userReward,
      userContributions,
      isOwner,
      hasClaimedReward,
      userBoost,
      contributeFunction,
      claimMiningRewardFunction,
      withdrawFunds,
      isTransactionPending,
      showStakingModal,
      setShowStakingModal,
      stakingContractRef,
      tokenContractRef,
      totalMiners,
      stakingPositions,
      setStakingPositions,
      totalStaked,
      setTotalStaked,
      fetchStakingPositions,
      fetchUserMiningData,
      isInitialized,
    ]
  );

  return (
    <MiningContext.Provider value={contextValue}>
      {isTransactionPending && (
        <div style={styles.overlay}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" color="white" style={{ marginTop: "10px" }}>
            Processing transaction...
          </Typography>
        </div>
      )}
      {children}
    </MiningContext.Provider>
  );
};

export default MiningProvider;